import axios from "axios";

// Languages used across multilingual { en, mk, sq } fields in the API data.
const LANGS = ["en", "mk", "sq"] as const;

// A "lang object" is a plain object that holds per-language values, e.g.
// { en: "Name", mk: "Име", sq: "Emri" }. We detect it by the presence of an
// `en` key (every multilingual field in the DB has English as the base).
function isLangObject(obj: any): boolean {
  return (
    obj &&
    typeof obj === "object" &&
    !Array.isArray(obj) &&
    Object.prototype.hasOwnProperty.call(obj, "en")
  );
}

// Recursively walk the API response and, for every multilingual field that is
// missing a translation (e.g. no Albanian yet), fall back to the first
// available language so the UI never renders blank content.
function fillFallback(data: any): any {
  if (Array.isArray(data)) {
    return data.map(fillFallback);
  }
  if (data && typeof data === "object") {
    if (isLangObject(data)) {
      const base = LANGS.map((l) => data[l]).find(
        (v) => v !== undefined && v !== null && v !== ""
      );
      for (const l of LANGS) {
        if (data[l] === undefined || data[l] === null || data[l] === "") {
          data[l] = base;
        }
      }
    }
    for (const key of Object.keys(data)) {
      if (data[key] && typeof data[key] === "object") {
        data[key] = fillFallback(data[key]);
      }
    }
  }
  return data;
}

// --- Cover axios calls (e.g. useProducts, useParentProducts) ---
axios.interceptors.response.use((response) => {
  if (response.data) {
    response.data = fillFallback(response.data);
  }
  return response;
});

// --- Cover native fetch calls (e.g. useAboutUs, useHistory, useAdminCareer) ---
// Many hooks use window.fetch instead of axios, so the interceptor above would
// miss them. We wrap fetch to post-process JSON responses with the same logic.
const originalFetch = window.fetch.bind(window);
window.fetch = async (...args: Parameters<typeof fetch>): Promise<Response> => {
  const response = await originalFetch(...args);

  const contentType = response.headers.get("content-type") || "";
  if (!contentType.includes("application/json")) {
    return response;
  }

  // Read a clone so the original body stays untouched if anything goes wrong.
  let data: any;
  try {
    data = await response.clone().json();
  } catch {
    return response; // empty or non-JSON body
  }

  const fixed = fillFallback(data);
  return new Response(JSON.stringify(fixed), {
    status: response.status,
    statusText: response.statusText,
    headers: response.headers,
  });
};

export {};
